/** Derive landing-page preview charts from live API payloads (no hardcoded demo values). */

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

export const deriveBlockOshHistogram = (blocks = []) => {
  if (!blocks.length) return null;
  const bins = [0, 0, 0, 0];
  for (const block of blocks) {
    const score = Number(block.shi_score ?? block.osh_score ?? 0);
    if (score < 25) bins[0] += 1;
    else if (score < 50) bins[1] += 1;
    else if (score < 75) bins[2] += 1;
    else bins[3] += 1;
  }
  const max = Math.max(...bins, 1);
  return [
    { label: "0–25", count: bins[0], heightPct: clamp(Math.round((bins[0] / max) * 100), 8, 100) },
    { label: "25–50", count: bins[1], heightPct: clamp(Math.round((bins[1] / max) * 100), 8, 100) },
    { label: "50–75", count: bins[2], heightPct: clamp(Math.round((bins[2] / max) * 100), 8, 100) },
    { label: "75–100", count: bins[3], heightPct: clamp(Math.round((bins[3] / max) * 100), 8, 100) },
  ];
};

/** Collapse SQAAF band distribution into three donut segments for the hero preview. */
export const deriveSqaafQualityDonut = (bandSummary = []) => {
  if (!Array.isArray(bandSummary) || !bandSummary.length) return null;
  const pct = (name) => {
    const row = bandSummary.find((b) => b.band === name);
    return Number(row?.pct ?? 0);
  };
  const strong = pct("Exemplary") + pct("Strong");
  const developing = pct("Developing");
  const needsWork = pct("Needs Focus") + pct("Critical");
  const total = strong + developing + needsWork;
  if (total <= 0) return null;
  return [
    { label: "Strong / Exemplary", pct: Math.round(strong * 10) / 10 },
    { label: "Developing", pct: Math.round(developing * 10) / 10 },
    { label: "Needs Focus", pct: Math.round(needsWork * 10) / 10 },
  ];
};

const LANDING_FEATURE_META = [
  { id: "udise", title: "UDISE Dashboard", path: "/executive-dashboard", icon: "udise", metricKey: "schools" },
  { id: "pgi", title: "PGI Dashboard", path: "/pgi-dashboard", icon: "pgi", metricKey: "pgi" },
  { id: "sqaf", title: "SQAAF Dashboard", path: "/sqaaf-dashboard", icon: "sqaf", metricKey: "sqaaf" },
  { id: "nas", title: "NAS Dashboard", path: "/nas-dashboard", icon: "nas", metricKey: "students" },
  { id: "nipun", title: "NIPUN Dashboard", path: "/nipun-dashboard", icon: "nipun", metricKey: "nipun" },
  { id: "ger", title: "GER Dashboard", path: "/ger-dashboard", icon: "ger", metricKey: "ger" },
  { id: "pat", title: "PAT Dashboard", path: "/pat-dashboard", icon: "pat", metricKey: "pat" },
  { id: "schools", title: "Schools Dashboard", path: "/infrastructure-dashboard", icon: "schools", metricKey: "water" },
  { id: "students", title: "Students Dashboard", path: "/enrolment-dashboard", icon: "students", metricKey: "students" },
  { id: "teachers", title: "Teachers Dashboard", path: "/teacher-dashboard", icon: "teachers", metricKey: "ptr" },
];

const formatLakhs = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return null;
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L students`;
  return `${n.toLocaleString("en-IN")} students`;
};

/** Navigation cards with descriptions derived from live overview metrics when available. */
export const deriveLandingFeatures = (liveKpis = null, sqaafBands = null, pgiOverview = null, gerOverview = null, nipunOverview = null, patOverview = null) => {
  const metrics = {
    schools: liveKpis?.total_schools
      ? `${liveKpis.total_schools.toLocaleString("en-IN")} schools in scope`
      : null,
    students: liveKpis?.total_students ? formatLakhs(liveKpis.total_students) : null,
    dataEntry:
      typeof liveKpis?.data_entry_percentage === "number"
        ? `${liveKpis.data_entry_percentage.toFixed(1)}% data entry completion`
        : null,
    osh:
      typeof liveKpis?.avg_shi === "number"
        ? `${liveKpis.avg_shi.toFixed(1)}% average OSH`
        : null,
    water:
      typeof liveKpis?.water_availability_percentage === "number"
        ? `${liveKpis.water_availability_percentage.toFixed(1)}% water availability`
        : null,
    ptr: typeof liveKpis?.avg_ptr === "number" ? `PTR ${liveKpis.avg_ptr.toFixed(1)} in scope` : null,
    sqaaf: (() => {
      const donut = deriveSqaafQualityDonut(sqaafBands || []);
      if (!donut?.length) return null;
      const strong = donut.find((d) => d.label.includes("Strong"))?.pct;
      return strong != null ? `${strong}% schools in strong/exemplary SQAAF bands` : null;
    })(),
    pgi:
      pgiOverview?.executive?.pgi_score != null
        ? `Pune PGI ${pgiOverview.executive.pgi_score} · Rank ${pgiOverview.ranking?.pune_rank ?? "—"} of ${pgiOverview.ranking?.total_districts ?? 36}`
        : null,
    ger:
      gerOverview?.overview?.metrics?.[2]?.value != null
        ? `State HS GER ${gerOverview.overview.metrics[2].value} · Pune rank ${gerOverview.district?.pune?.hs_rank ?? "—"}`
        : null,
    nipun:
      nipunOverview?.executive?.nipun_rate != null
        ? `State NIPUN ${nipunOverview.executive.nipun_rate}% · Pune ${nipunOverview.district?.pune?.nipun_rate ?? "—"}%`
        : null,
    pat:
      patOverview?.executive?.pat3_avg != null
        ? `PAT-3 ${patOverview.executive.pat3_avg}% · ${patOverview.executive.growth_pp >= 0 ? "+" : ""}${patOverview.executive.growth_pp} pp vs PAT-2`
        : null,
  };

  const defaults = {
    udise: "Unified District Information System for education data and indicators.",
    pgi: "Pune district Performance Grading Index — scores, domains, and YoY improvement.",
    sqaf: "School Quality Assessment and Accreditation framework.",
    nas: "National Achievement Survey outcomes and learning levels.",
    nipun: "Reading with understanding and numeracy proficiency monitoring.",
    ger: "Gross Enrolment Ratio 2025–26 — stage-wise, district and gender equity views.",
    pat: "Periodic Assessment Test readiness across data completion and identity compliance.",
    schools: "School-level data, infrastructure, and location insights.",
    students: "Enrolment, attendance, and student-level analytics.",
    teachers: "Teacher deployment, qualifications, and capacity.",
  };

  return LANDING_FEATURE_META.map((feature) => ({
    id: feature.id,
    title: feature.title,
    path: feature.path,
    icon: feature.icon,
    description: metrics[feature.metricKey] || defaults[feature.id] || "Live district analytics dashboard.",
  }));
};

export const deriveDonutGradient = (segments) => {
  if (!segments?.length) return null;
  const total = segments.reduce((s, seg) => s + seg.pct, 0) || 1;
  let cursor = 0;
  const stops = segments.map((seg, i) => {
    const share = (seg.pct / total) * 100;
    const start = cursor;
    cursor += share;
    const colors = ["var(--primary)", "var(--accent)", "var(--secondary)"];
    return `${colors[i % colors.length]} ${start}% ${cursor}%`;
  });
  return `conic-gradient(${stops.join(", ")})`;
};
